import type { Attributes } from './combat-types.js';
import type { ClassDefinition, Stat } from './class-types.js';
import type { BodyShape, FaceShape } from './world-types.js';

/**
 * A player-made level 1 hero (Core Rules pp. 18–23): class, ancestry, stat array, skill points and looks.
 * The browser saves builds to the account; the server rebuilds every hero it deploys from one and trusts nothing else.
 */
export const STATS: Stat[] = ['strength', 'dexterity', 'intelligence', 'will'];
export const STAT_LABELS: Record<Stat, string> = { strength: 'STR', dexterity: 'DEX', intelligence: 'INT', will: 'WIL' };

/** Core Rules p. 20: the three stat arrays. */
export type StatArrayId = 'standard' | 'balanced' | 'min-max';
export const STAT_ARRAYS: Record<StatArrayId, { name: string; values: [number, number, number, number] }> = {
  standard: { name: 'Standard', values: [2, 2, 0, -1] },
  balanced: { name: 'Balanced', values: [2, 1, 1, 0] },
  'min-max': { name: 'Min-Max', values: [3, 1, -1, -1] },
};

/** Core Rules p. 7. Each skill starts at its stat. */
export type SkillId = 'arcana' | 'examination' | 'finesse' | 'influence' | 'insight' | 'lore' | 'might' | 'naturecraft' | 'perception' | 'stealth';
export const SKILLS: { id: SkillId; name: string; stat: Stat }[] = [
  { id: 'arcana', name: 'Arcana', stat: 'intelligence' }, { id: 'examination', name: 'Examination', stat: 'intelligence' },
  { id: 'finesse', name: 'Finesse', stat: 'dexterity' }, { id: 'influence', name: 'Influence', stat: 'will' },
  { id: 'insight', name: 'Insight', stat: 'will' }, { id: 'lore', name: 'Lore', stat: 'intelligence' },
  { id: 'might', name: 'Might', stat: 'strength' }, { id: 'naturecraft', name: 'Naturecraft', stat: 'will' },
  { id: 'perception', name: 'Perception', stat: 'will' }, { id: 'stealth', name: 'Stealth', stat: 'dexterity' },
];
export const MAX_NAME_LENGTH = 24;

/** Replaceable ancestry content. Only what the rules engine can apply today. */
export interface AncestryDefinition {
  id: string; name: string; size: 'small' | 'medium'; trait: string; description: string;
  skillBonus: number; initiativeBonus: number;
  /** Roll two d20s and keep the higher for Initiative. */
  initiativeAdvantage?: boolean;
  /** Extra movement spaces (each 1.5 m). */
  speedBonus?: number;
  maxHitDiceBonus?: number;
  maxWoundsBonus?: number;
  traitDescription?: string;
  skillBonuses?: Partial<Record<SkillId, number>>;
  /** A survival charge, restored only by a completed Safe Rest. */
  relentless?: boolean;
  skedaddle?: boolean;
  /** Highborn: the class's disadvantaged save is rolled neutral instead. */
  highborn?: boolean;
  /** Elusive: once per Safe Rest a failed save succeeds instead. */
  elusive?: boolean;
  languages?: { id: string; name: string; minimumIntelligence: number }[];
  languageDescription?: string;
  skinColors?: number[];
  hairColors?: number[];
}

export interface CharacterLook {
  /** The rig family, which is also the model asset. */
  body: string;
  skin: number; hair: number; cloth: number; leather: number;
  /** Rings, routes and plates. */
  color: number;
  /** Metres. */
  height: number;
  /** The body sliders (`BodyShape`); characters saved before them have none and keep their body's own build. */
  shape?: BodyShape;
  /** The face sliders (`FaceShape`); a slider left out is neutral, or for wrinkles and sag follows the body's age. */
  faceShape?: Partial<FaceShape>;
  /** Interchangeable features and cosmetics by slot, each a variant id from content. */
  parts: Record<string, string>;
  /** The painted portrait's id: its file name in `src/client/assets/portraits` without the extension. Absent shows the class glyph. */
  portrait?: string;
}
/** A portrait id: lower-case letters, digits and hyphens, the way the portrait folder's files are named. */
export const PORTRAIT_ID = /^[a-z0-9][a-z0-9-]{0,63}$/;
/** A voice pack a character can speak with (`src/content/voices`). `bodies` are the rigs it suits, which only picks the default. */
export interface VoiceOption { id: string; name: string; bodies: string[] }
/** The voice a body gets unless another is chosen: the first that suits it, else the first offered. */
export function defaultVoice(options: Pick<CharacterOptions, 'voices'>, body: string): string {
  return (options.voices.find(voice => voice.bodies.includes(body)) ?? options.voices[0])?.id ?? '';
}
export interface CharacterBuild {
  /** Stable per account; assigned on first save. */
  id: string;
  name: string;
  classId: string;
  ancestryId: string;
  /** Backgrounds are not offered yet. */
  backgroundId: null;
  statArray: StatArrayId;
  attributes: Attributes;
  look: CharacterLook;
  /** The voice pack heroes of this character answer orders with. */
  voice: string;
}

/** One look of a part slot; `bodies` limits it to some bodies, otherwise every body offers it. */
export interface PartVariant { id: string; name: string; bodies?: string[]; legacyIds?: string[] }
/** What the content offers; the validator accepts nothing else. */
export interface CharacterOptions {
  classes: ClassDefinition[];
  ancestries: AncestryDefinition[];
  bodies: { id: string; name: string; height: [number, number]; ancestryId?: string }[];
  /** Part slots by id, each with its display name and variants. A slot's first variant on a body is its default there. */
  parts: Record<string, { name: string; variants: PartVariant[]; defaultByBody?: Record<string, string>; defaultBy?: { slot: string; values: Record<string, string> } }>;
  voices: VoiceOption[];
}
/** The variants of a part slot a body offers, default first. */
export function partVariants(options: Pick<CharacterOptions, 'parts'>, slot: string, body: string): PartVariant[] {
  const part = options.parts[slot], variants = part?.variants.filter(variant => !variant.bodies || variant.bodies.includes(body)) ?? [];
  const preferred = part?.defaultByBody?.[body];
  return preferred ? [...variants.filter(variant => variant.id === preferred), ...variants.filter(variant => variant.id !== preferred)] : variants;
}

/** The book's advice: the best values go to the class's Key stats, the worst to its disadvantaged save. */
export function suggestedAttributes(arrayId: StatArrayId, cls: Pick<ClassDefinition, 'keyStats' | 'saves'>): Attributes {
  const values = [...STAT_ARRAYS[arrayId].values];
  const others = STATS.filter(stat => !cls.keyStats.includes(stat))
    .sort((a, b) => Number(cls.saves.disadvantage.includes(a)) - Number(cls.saves.disadvantage.includes(b)));
  const order = [...cls.keyStats, ...others];
  return Object.fromEntries(order.map((stat, index) => [stat, values[index]])) as unknown as Attributes;
}

/** Core Rules 3.2 p. 20: a skill is its stat plus ancestry bonuses; the optional extra skill points are not used. */
export function skillTotals(build: Pick<CharacterBuild, 'attributes'>, ancestry: Pick<AncestryDefinition, 'skillBonus' | 'skillBonuses'>): Record<SkillId, number> {
  return Object.fromEntries(SKILLS.map(skill => [skill.id, build.attributes[skill.stat] + ancestry.skillBonus + (ancestry.skillBonuses?.[skill.id] ?? 0)])) as Record<SkillId, number>;
}
/** Canonical language ids carry interoperability; names retain an ancestry's own terminology. */
export function ancestryLanguages(attributes: Attributes, ancestry: Pick<AncestryDefinition, 'languages'>): { id: string; name: string }[] {
  return (ancestry.languages ?? []).filter(language => attributes.intelligence >= language.minimumIntelligence).map(({ id, name }) => ({ id, name }));
}
export function ancestryBodies(options: Pick<CharacterOptions, 'bodies'>, ancestryId: string) {
  return options.bodies.filter(body => !body.ancestryId || body.ancestryId === ancestryId);
}

const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const color = (value: unknown): value is number => Number.isInteger(value) && (value as number) >= 0 && (value as number) <= 0xffffff;
const sorted = (values: number[]) => [...values].sort((a, b) => a - b);
const sameValues = (a: number[], b: number[]) => a.length === b.length && sorted(a).every((value, index) => value === sorted(b)[index]);

/** The body sliders' ranges (`content/characters/rigs/humanoid-shape.ts` reads them). */
export const BODY_SHAPE_RANGES: Record<keyof BodyShape, [number, number]> = { weight: [-1, 1], muscle: [-1, 1], frame: [-1, 1], age: [0, 1] };
/** A saved shape with every slider inside its range, rounded to the creator's step; anything else is refused. */
function validShape(value: unknown): BodyShape {
  if (!record(value) || Object.keys(value).some(key => !(key in BODY_SHAPE_RANGES))) throw new Error('Choose a body shape within the sliders’ range.');
  const shape = {} as BodyShape;
  for (const [key, [min, max]] of Object.entries(BODY_SHAPE_RANGES) as [keyof BodyShape, [number, number]][]) {
    const slider = value[key] ?? 0;
    if (typeof slider !== 'number' || !(slider >= min && slider <= max)) throw new Error('Choose a body shape within the sliders’ range.');
    shape[key] = Math.round(slider * 100) / 100;
  }
  return shape;
}
/** The face sliders' ranges (`content/characters/rigs/humanoid-face-shape.ts` reads them). */
export const FACE_SHAPE_RANGES: Record<keyof FaceShape, [number, number]> = {
  length: [-1, 1], width: [-1, 1], jaw: [-1, 1], chin: [-1, 1], cheekbones: [-1, 1], brow: [-1, 1], noseSize: [-1, 1], noseWidth: [-1, 1], noseBridge: [-1, 1],
  eyeSpacing: [-1, 1], eyeDepth: [-1, 1], eyeSize: [-1, 1], lips: [-1, 1], wrinkles: [0, 1], sag: [0, 1],
};
/** Saved face sliders, each inside its range and rounded to the creator's step; sliders left out stay out. Anything else is refused. */
function validFaceShape(value: unknown): Partial<FaceShape> {
  if (!record(value) || Object.keys(value).some(key => !(key in FACE_SHAPE_RANGES))) throw new Error('Choose a face within the sliders’ range.');
  const shape: Partial<FaceShape> = {};
  for (const [key, slider] of Object.entries(value) as [keyof FaceShape, unknown][]) {
    const [min, max] = FACE_SHAPE_RANGES[key];
    if (typeof slider !== 'number' || !(slider >= min && slider <= max)) throw new Error('Choose a face within the sliders’ range.');
    shape[key] = Math.round(slider * 100) / 100;
  }
  return shape;
}
/**
 * Checks an untrusted build against the creation rules and returns a clean copy with only known fields.
 * Throws a player-facing message for the first problem found.
 */
export function validateCharacterBuild(value: unknown, options: CharacterOptions): CharacterBuild {
  if (!record(value)) throw new Error('This character could not be read.');
  const { id, name, classId, ancestryId, statArray, attributes, look } = value;
  if (typeof id !== 'string' || !/^[A-Za-z0-9_-]{1,40}$/.test(id)) throw new Error('This character has no valid id.');
  if (typeof name !== 'string' || !name.trim() || name.trim().length > MAX_NAME_LENGTH || /[\u0000-\u001f<>]/.test(name)) throw new Error(`Give your character a name of up to ${MAX_NAME_LENGTH} characters.`);
  const cls = options.classes.find(candidate => candidate.id === classId);
  if (!cls) throw new Error('Choose one of the available classes.');
  if (!options.ancestries.some(ancestry => ancestry.id === ancestryId)) throw new Error('Choose one of the available ancestries.');
  if (value.backgroundId !== null && value.backgroundId !== undefined) throw new Error('Backgrounds are not available yet.');
  if (typeof statArray !== 'string' || !(statArray in STAT_ARRAYS)) throw new Error('Choose a stat array.');
  if (!record(attributes) || !STATS.every(stat => Number.isInteger(attributes[stat])) ||
    !sameValues(STATS.map(stat => attributes[stat] as number), STAT_ARRAYS[statArray as StatArrayId].values)) throw new Error('Place each value of the stat array on one stat.');
  const stats = Object.fromEntries(STATS.map(stat => [stat, attributes[stat]])) as unknown as Attributes;
  // Characters saved with the old extra skill points simply drop them.
  const ancestry = options.ancestries.find(candidate => candidate.id === ancestryId)!;
  if (!record(look)) throw new Error('Choose how your character looks.');
  const body = options.bodies.find(candidate => candidate.id === look.body);
  if (!body || body.ancestryId && body.ancestryId !== ancestry.id) throw new Error('Choose one of the available bodies for your ancestry.');
  if (!['skin', 'hair', 'cloth', 'leather', 'color'].every(key => color(look[key]))) throw new Error('Choose valid colors.');
  if (typeof look.height !== 'number' || !(look.height >= body.height[0] && look.height <= body.height[1])) throw new Error('Choose a height within the body’s range.');
  const shape = look.shape === undefined ? undefined : validShape(look.shape);
  const faceShape = look.faceShape === undefined ? undefined : validFaceShape(look.faceShape);
  if (!record(look.parts)) throw new Error('Choose your character’s features.');
  const parts: Record<string, string> = {};
  for (const slot of Object.keys(options.parts)) {
    // A slot added after a character was saved takes its default.
    const variants = partVariants(options, slot, body.id), dependency = options.parts[slot].defaultBy;
    const inherited = dependency && typeof look.parts[dependency.slot] === 'string' ? dependency.values[look.parts[dependency.slot] as string] : undefined;
    const choice = look.parts[slot] ?? inherited ?? variants[0]?.id;
    const selected = variants.find(variant => variant.id === choice || typeof choice === 'string' && variant.legacyIds?.includes(choice));
    if (!selected) throw new Error('Choose one of the available features.');
    parts[slot] = selected.id;
  }
  if (Object.keys(look.parts).some(slot => !(slot in options.parts))) throw new Error('Choose one of the available features.');
  // The server cannot see the browser's portrait files, so only the id's form is checked; an unknown one shows the class glyph.
  if (look.portrait !== undefined && (typeof look.portrait !== 'string' || !PORTRAIT_ID.test(look.portrait))) throw new Error('Choose one of the available portraits.');
  // Characters saved before voices existed take their body's default.
  const voice = value.voice ?? defaultVoice(options, body.id);
  if (typeof voice !== 'string' || (options.voices.length > 0 || voice !== '') && !options.voices.some(option => option.id === voice)) throw new Error('Choose one of the available voices.');
  return {
    id, name: name.trim(), classId: cls.id, ancestryId: ancestry.id, backgroundId: null, statArray: statArray as StatArrayId, attributes: stats,
    look: { body: body.id, skin: look.skin as number, hair: look.hair as number, cloth: look.cloth as number, leather: look.leather as number, color: look.color as number, height: Math.round(look.height * 100) / 100, ...shape ? { shape } : {}, ...faceShape ? { faceShape } : {}, parts, ...look.portrait ? { portrait: look.portrait as string } : {} }, voice,
  };
}
